import React, { useContext, useEffect, useState, useRef } from 'react';
import AgreementModal from '../components/AgreementModal.js';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import {
  User, Mail, Phone, Award, Flame, Trophy, BookOpen, ChevronLeft, Edit, Save, X,
  TrendingUp, CheckCircle, Sparkles, Mic, Headphones, PenTool, FileText, Plane,
  Target, Zap, Star, Clock, Calendar, BarChart3, Volume2, MessageCircle, AlertCircle, Send, Download, FileDown,
  CreditCard, Loader2, Camera, Trash2, GraduationCap, Upload, ChevronRight
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [gamificationProfile, setGamificationProfile] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [flightTickets, setFlightTickets] = useState({ routes: [], collected_count: 0 });
  const [examResults, setExamResults] = useState([]);
  const [speakingHistory, setSpeakingHistory] = useState([]);
  const [listeningHistory, setListeningHistory] = useState([]);
  const [readingHistory, setReadingHistory] = useState([]);
  const [writingHistory, setWritingHistory] = useState([]);
  const [levelProgress, setLevelProgress] = useState({});
  const [milestones, setMilestones] = useState([]);
  const [cabinProgress, setCabinProgress] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const [myCertificates, setMyCertificates] = useState([]);
  
  // Sözleşme ve ödeme bilgileri
  const [agreementInfo, setAgreementInfo] = useState(null);
  
  // Danışman ve mesajlaşma state'leri
  const [advisorMessages, setAdvisorMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadAdvisorCount, setUnreadAdvisorCount] = useState(0);
  
  // Sorun bildir state'leri
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportType, setReportType] = useState('bug');
  const [sendingReport, setSendingReport] = useState(false);
  
  // Ödeme yapma modal state'leri
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [myDocuments, setMyDocuments] = useState({ photo: null, diploma: null, id_card: null });
  const [myAgreements, setMyAgreements] = useState({ agreements_accepted: false, sales_agreement: "", distance_agreement: "" });
  const [showAgreementModal, setShowAgreementModal] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  const [myRecordings, setMyRecordings] = useState({ recordings: [], total: 0 });
  const [myVideos, setMyVideos] = useState({ videos: [], remaining_credits: 0 });
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);
  
  // Debug log for agreement modal
  useEffect(() => {
    console.log('Profile - showAgreementModal:', showAgreementModal);
    console.log('Profile - myAgreements:', myAgreements);
  }, [showAgreementModal, myAgreements]);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Delete recording function
  const deleteRecording = async (recordingId) => {
    if (!window.confirm("Bu kaydı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/voice-recordings/${recordingId}`, { withCredentials: true });
      toast.success("Kayıt silindi");
      // Refresh recordings
      const response = await axios.get(`${API}/skill-tests/speaking/my-recordings`, { withCredentials: true });
      setMyRecordings(response.data || { recordings: [], by_level: {}, total: 0 });
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Kayıt silinemedi");
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        gamificationRes,
        flightRes,
        examRes,
        speakingRes,
        listeningRes,
        readingRes,
        writingRes,
        statsRes,
        cabinRes,
        videoRes,
        certsRes,
        agreementRes,
        advisorMsgsRes,
        unreadCountRes,
        documentsRes,
        recordingsRes,
        videosRes,
        leaderboardRes,
      ] = await Promise.all([
        axios.get(`${API}/gamification/profile`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/gamification/flight-tickets`, { withCredentials: true }).catch(() => ({ data: { routes: [] } })),
        axios.get(`${API}/exam-results`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/speaking/history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/listening/history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/reading/history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/writing/history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/kariyer/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/cabin-crew/progress`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/video-courses/my-progress`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/certificates/my`, { withCredentials: true }).catch(() => ({ data: { certificates: [] } })),
        axios.get(`${API}/user/agreement-info`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/advisor/messages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/advisor/unread-count`, { withCredentials: true }).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/documents/my`, { withCredentials: true }).catch(() => ({ data: { photo: null, diploma: null, id_card: null } })),
        axios.get(`${API}/skill-tests/speaking/my-recordings`, { withCredentials: true }).catch(() => ({ data: { recordings: [], total: 0 } })),
        axios.get(`${API}/video-interview/my-videos`, { withCredentials: true }).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/gamification/leaderboard`, { withCredentials: true }).catch(() => ({ data: { user_rank: null } }))
      ]);

      setGamificationProfile(gamificationRes.data);
      setFlightTickets(flightRes.data);
      setExamResults(Array.isArray(examRes.data) ? examRes.data : []);
      setSpeakingHistory(Array.isArray(speakingRes.data) ? speakingRes.data : []);
      setListeningHistory(Array.isArray(listeningRes.data) ? listeningRes.data : []);
      setReadingHistory(Array.isArray(readingRes.data) ? readingRes.data : []);
      setWritingHistory(Array.isArray(writingRes.data) ? writingRes.data : []);
      setLevelProgress(statsRes.data?.level_progress || {});
      setCabinProgress(cabinRes.data);
      setVideoProgress(videoRes.data);
      setMyCertificates(certsRes.data?.certificates || []);
      setAgreementInfo(agreementRes.data);
      
      // Danışman mesajlarını yükle
      const msgs = Array.isArray(advisorMsgsRes.data) ? advisorMsgsRes.data : [];
      setAdvisorMessages(msgs.map(m => ({
        sender: m.sender || 'student',
        message: m.message,
        timestamp: m.created_at ? new Date(m.created_at).toLocaleString('tr-TR') : '',
        advisorName: m.advisor_name
      })));
      
      // Okunmamış mesaj sayısını ayarla
      setUnreadAdvisorCount(unreadCountRes.data?.unread_count || 0);
      setMyDocuments(documentsRes.data || { photo: null, diploma: null, id_card: null });
      
      // Sözleşmeleri yükle
      try {
        const agreementsRes = await axios.get(`${API}/my-agreements`, { withCredentials: true });
        setMyAgreements(agreementsRes.data);
      } catch (e) { console.log("Agreements not loaded"); }
      console.log('Recordings API response:', recordingsRes.data);
      setMyRecordings(recordingsRes.data || { recordings: [], total: 0 });
      setMyVideos(videosRes.data || { videos: [] });
      setUserRank(leaderboardRes.data?.user_rank || null);
      
      // Calculate milestones
      calculateMilestones(gamificationRes.data, examRes.data, speakingRes.data);
      
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB den küçük olmalıdır');
      return;
    }
    setDocUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64 = event.target.result;
          const formData = new FormData();
          formData.append('doc_type', docType);
          formData.append('file_data', base64);
          formData.append('filename', file.name);
          
          const response = await axios.post(API + '/documents/upload', formData, { 
            withCredentials: true
            // Content-Type otomatik ayarlanacak - manuel belirtme!
          });
          
          if (response.data.success) {
            const docsRes = await axios.get(API + '/documents/my', { withCredentials: true });
            setMyDocuments(docsRes.data);
            toast.success('Belge başarıyla yüklendi!');
          }
        } catch (err) {
          console.error('Upload error:', err);
          toast.error(err.response?.data?.detail || 'Belge yüklenirken bir hata oluştu');
        }
        setDocUploading(false);
      };
      reader.onerror = () => {
        toast.error('Dosya okunamadı');
        setDocUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Belge yüklenirken bir hata oluştu');
      setDocUploading(false);
    }
  };

  const calculateMilestones = (gProfile, exams, speaking) => {
    const miles = [];
    if (gProfile?.total_miles >= 100) miles.push({ icon: '🎯', title: '100 Mil', date: 'Tamamlandı' });
    if (gProfile?.total_miles >= 500) miles.push({ icon: '🚀', title: '500 Mil', date: 'Tamamlandı' });
    if (gProfile?.total_miles >= 1000) miles.push({ icon: '⭐', title: '1000 Mil', date: 'Tamamlandı' });
    if ((exams?.length || 0) >= 1) miles.push({ icon: '📝', title: 'İlk Sınav', date: 'Tamamlandı' });
    if ((speaking?.length || 0) >= 1) miles.push({ icon: '🎤', title: 'İlk Konuşma', date: 'Tamamlandı' });
    if (gProfile?.streak?.current >= 7) miles.push({ icon: '🔥', title: '7 Gün Seri', date: 'Tamamlandı' });
    setMilestones(miles);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, formData, { withCredentials: true });
      if (response.data.user) {
        setUser(response.data.user);
        toast.success('Profil güncellendi!');
        setEditing(false);
      }
    } catch (error) {
      toast.error('Profil güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // Calculate total points from different sources
  const pointsBreakdown = [
    { name: 'Sınavlar', value: examResults.length * 50, color: '#3B82F6', icon: '📝' },
    { name: 'Speaking', value: speakingHistory.length * 30, color: '#10B981', icon: '🎤' },
    { name: 'Listening', value: listeningHistory.length * 20, color: '#8B5CF6', icon: '🎧' },
    { name: 'Reading', value: readingHistory.length * 20, color: '#F59E0B', icon: '📖' },
    { name: 'Writing', value: writingHistory.length * 20, color: '#EC4899', icon: '✍️' },
    { name: 'Bonus', value: gamificationProfile?.total_miles ? Math.floor(gamificationProfile.total_miles * 0.1) : 0, color: '#06B6D4', icon: '🎁' }
  ].filter(p => p.value > 0);

  // Skill progress data for radar/bar chart
  const skillProgress = [
    { skill: 'Sınavlar', score: Math.min(100, examResults.length * 10), fullMark: 100 },
    { skill: 'Speaking', score: Math.min(100, speakingHistory.length * 15), fullMark: 100 },
    { skill: 'Listening', score: Math.min(100, listeningHistory.length * 15), fullMark: 100 },
    { skill: 'Reading', score: Math.min(100, readingHistory.length * 15), fullMark: 100 },
    { skill: 'Writing', score: Math.min(100, writingHistory.length * 15), fullMark: 100 }
  ];

  // Level progress for English
  const englishLevels = [
    { id: 'level_0', name: 'Başlangıç', short: 'L0' },
    { id: 'level_1', name: 'A1', short: 'A1' },
    { id: 'level_2', name: 'A2', short: 'A2' },
    { id: 'level_3', name: 'B1', short: 'B1' },
    { id: 'level_4', name: 'B2', short: 'B2' },
    { id: 'level_5', name: 'Havacılık', short: 'Av' },
    { id: 'level_6', name: 'THY', short: 'THY' },
    { id: 'level_7', name: 'Mülakat', short: 'Int' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-blue-200">Profil yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-r from-blue-600/90 to-purple-600/90 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span>Ana Sayfa</span>
            </button>
            <h1 className="text-xl font-bold text-white">Profilim</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Profile Hero */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 -mt-2">
        <Card className="border-0 shadow-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-5xl font-bold text-white shadow-xl">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full px-3 py-1 text-xs font-bold text-white shadow-lg">
                  Seviye {gamificationProfile?.career_level?.level || 1}
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                {editing ? (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} placeholder="Ad" className="bg-white/20 border-white/30 text-white placeholder:text-white/50" />
                      <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} placeholder="Soyad" className="bg-white/20 border-white/30 text-white placeholder:text-white/50" />
                    </div>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Telefon" className="bg-white/20 border-white/30 text-white placeholder:text-white/50" />
                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={saving} size="sm" className="bg-green-500 hover:bg-green-600">
                        <Save className="w-4 h-4 mr-1" /> Kaydet
                      </Button>
                      <Button onClick={() => setEditing(false)} size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                        <X className="w-4 h-4 mr-1" /> İptal
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-1">{user?.name || 'Kullanıcı'}</h2>
                    <p className="text-blue-200 text-sm flex items-center justify-center md:justify-start gap-2">
                      <Mail className="w-4 h-4" /> {user?.email}
                    </p>
                    {user?.phone && (
                      <p className="text-blue-200 text-sm flex items-center justify-center md:justify-start gap-2 mt-1">
                        <Phone className="w-4 h-4" /> {user.phone}
                      </p>
                    )}
                    <Button onClick={() => setEditing(true)} size="sm" variant="outline" className="mt-3 border-white/30 text-white hover:bg-white/10">
                      <Edit className="w-4 h-4 mr-1" /> Düzenle
                    </Button>
                  </>
                )}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur">
                  <div className="text-3xl font-bold text-white">{gamificationProfile?.total_miles || 0}</div>
                  <div className="text-xs text-blue-200 flex items-center justify-center gap-1"><Plane className="w-3 h-3" /> Mil</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur">
                  <div className="text-3xl font-bold text-orange-400">{gamificationProfile?.streak?.current || 0}</div>
                  <div className="text-xs text-blue-200 flex items-center justify-center gap-1"><Flame className="w-3 h-3" /> Gün Serisi</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur">
                  <div className="text-3xl font-bold text-amber-400">{gamificationProfile?.career_level?.level || 1}</div>
                  <div className="text-xs text-blue-200 flex items-center justify-center gap-1"><Trophy className="w-3 h-3" /> Kariyer</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur">
                  <div className="text-3xl font-bold text-sky-400">{flightTickets.collected_count || 0}/{flightTickets.total_count || 10}</div>
                  <div className="text-xs text-blue-200 flex items-center justify-center gap-1"><Plane className="w-3 h-3" /> Rota</div>
                </div>
              </div>
              
              {/* User Rank */}
              {userRank && (
                <div className="mt-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-500/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-yellow-400 font-bold text-xl">#{userRank}</p>
                        <p className="text-xs text-yellow-200/70">Genel Siralama</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-yellow-200/70">Mil kazanarak siralamanizi yukseltebilirsiniz!</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Career Level Progress */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium flex items-center gap-2">
                  <span className="text-xl">{gamificationProfile?.career_level?.icon || '✈️'}</span>
                  {gamificationProfile?.career_level?.name || 'Kabin Adayı'}
                </span>
                <span className="text-blue-200 text-sm">
                  {gamificationProfile?.miles_to_next || 0} mil sonraki seviyeye
                </span>
              </div>
              <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-1000"
                  style={{ width: `${gamificationProfile?.level_progress || 0}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 mt-6">
        {/* Mobile-friendly tab navigation */}
        <div className="relative">
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory">
            {[
              { id: 'overview', label: 'Genel', mobileLabel: 'Genel', icon: BarChart3 },
              { id: 'recordings', label: 'Kayıtlarım', mobileLabel: 'Kayıt', icon: Mic },
              { id: 'documents', label: 'Belgelerim', mobileLabel: 'Belge', icon: Upload },
              
              { id: 'certificates', label: 'Sertifikalarım', mobileLabel: 'Sertifika', icon: Award },
              { id: 'payments', label: 'Ödemelerim', mobileLabel: 'Ödeme', icon: FileText },
              { id: 'advisor', label: 'Danışman', mobileLabel: 'Danış', icon: MessageCircle }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={async () => {
                  setActiveTab(tab.id);
                  // Danışman sekmesine tıklanınca mesajları okundu olarak işaretle
                  if (tab.id === 'advisor' && unreadAdvisorCount > 0) {
                    try {
                      await axios.post(`${API}/advisor/mark-read`, {}, { withCredentials: true });
                      setUnreadAdvisorCount(0);
                    } catch (err) {
                      console.error('Mesajlar okundu işaretlenemedi:', err);
                    }
                  }
                }}
                className={`relative flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 snap-start ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-lg'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.mobileLabel}</span>
                {/* Okunmamış mesaj badge'i */}
                {tab.id === 'advisor' && unreadAdvisorCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadAdvisorCount}
                  </span>
                )}
              </button>
            ))}
          </div>
          {/* Fade effect to indicate more tabs */}
          <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-blue-600/80 to-transparent pointer-events-none md:hidden" />
        </div>
        {/* Scroll indicator for mobile */}
        <div className="flex justify-center mt-2 md:hidden">
          <div className="flex gap-1">
            {[0,1,2,3,4,5,6].map(i => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === ['overview','recordings','documents','certificates','payments','advisor'].indexOf(activeTab) ? 'bg-white scale-125' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 mt-6 pb-8">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Activity Summary */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg col-span-full">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Aktivite Özeti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-blue-500/20 rounded-xl p-4 text-center">
                    <FileText className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{examResults.length}</div>
                    <div className="text-xs text-blue-200">Sınav</div>
                  </div>
                  <div className="bg-green-500/20 rounded-xl p-4 text-center">
                    <Mic className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{speakingHistory.length}</div>
                    <div className="text-xs text-green-200">Speaking</div>
                  </div>
                  <div className="bg-purple-500/20 rounded-xl p-4 text-center">
                    <Headphones className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{listeningHistory.length}</div>
                    <div className="text-xs text-purple-200">Listening</div>
                  </div>
                  <div className="bg-amber-500/20 rounded-xl p-4 text-center">
                    <BookOpen className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{readingHistory.length}</div>
                    <div className="text-xs text-amber-200">Reading</div>
                  </div>
                  <div className="bg-pink-500/20 rounded-xl p-4 text-center">
                    <PenTool className="w-8 h-8 text-pink-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{writingHistory.length}</div>
                    <div className="text-xs text-pink-200">Writing</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* English Level Progress */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg col-span-full md:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-400" />
                  İngilizce Seviye İlerlemesi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  {englishLevels.map((level, idx) => {
                    const progress = levelProgress[level.id] || 0;
                    const isCompleted = progress >= 100;
                    const isCurrent = !isCompleted && (idx === 0 || (levelProgress[englishLevels[idx-1]?.id] || 0) >= 100);
                    
                    return (
                      <div key={level.id} className="flex flex-col items-center min-w-[60px]">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          isCompleted ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow-lg shadow-green-500/30' :
                          isCurrent ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white shadow-lg shadow-blue-500/30 animate-pulse' :
                          'bg-white/10 text-white/50'
                        }`}>
                          {isCompleted ? '✓' : level.short}
                        </div>
                        <span className={`text-xs mt-1 ${isCompleted ? 'text-green-400' : isCurrent ? 'text-blue-400' : 'text-white/50'}`}>
                          {level.name}
                        </span>
                        {isCurrent && (
                          <span className="text-xs text-blue-300">{progress}%</span>
                        )}
                        {idx < englishLevels.length - 1 && (
                          <div className={`absolute w-8 h-0.5 translate-x-10 ${isCompleted ? 'bg-green-400' : 'bg-white/20'}`} style={{ marginTop: '24px' }}></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Kabin Memurluğu İlerlemesi */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plane className="w-5 h-5 text-emerald-400" />
                  Kabin Memurluğu Eğitimi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Progress Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-500/20 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">
                        {cabinProgress?.completed_lessons || cabinProgress?.completed_modules?.length || 0}
                      </p>
                      <p className="text-xs text-white/70">Tamamlanan Ders</p>
                    </div>
                    <div className="bg-teal-500/20 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-teal-400">
                        {cabinProgress?.total_lessons || cabinProgress?.total_modules || 22}
                      </p>
                      <p className="text-xs text-white/70">Toplam Ders</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">İlerleme</span>
                      <span className="text-emerald-400 font-bold">
                        {cabinProgress?.total_lessons > 0 
                          ? Math.round(((cabinProgress?.completed_lessons || 0) / cabinProgress.total_lessons) * 100)
                          : cabinProgress?.percentage || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-emerald-400 to-teal-500 h-3 rounded-full transition-all"
                        style={{ 
                          width: `${cabinProgress?.total_lessons > 0 
                            ? Math.round(((cabinProgress?.completed_lessons || 0) / cabinProgress.total_lessons) * 100)
                            : cabinProgress?.percentage || 0}%` 
                        }}
                      />
                    </div>
                  </div>

                  {/* Module Categories */}
                  <div className="space-y-2">
                    <p className="text-xs text-white/50 uppercase tracking-wide">Modüller</p>
                    <div className="flex flex-wrap gap-2">
                      {['Temel', 'Emniyet', 'Servis', 'Anonslar'].map((mod, idx) => (
                        <span key={idx} className="px-2 py-1 bg-white/10 rounded-lg text-xs text-white/70">
                          {mod}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Videolu Dersler İlerlemesi */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <span className="text-purple-400">📹</span>
                  Videolu Dersler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Video Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-purple-500/20 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-purple-400">
                        {videoProgress?.watched_count || 0}
                      </p>
                      <p className="text-xs text-white/70">İzlenen Video</p>
                    </div>
                    <div className="bg-violet-500/20 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-violet-400">
                        {videoProgress?.total_videos || 0}
                      </p>
                      <p className="text-xs text-white/70">Toplam Video</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/70">İzleme Oranı</span>
                      <span className="text-purple-400 font-bold">
                        {videoProgress?.total_videos > 0 
                          ? Math.round((videoProgress.watched_count / videoProgress.total_videos) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-400 to-violet-500 h-3 rounded-full transition-all"
                        style={{ 
                          width: `${videoProgress?.total_videos > 0 
                            ? Math.round((videoProgress.watched_count / videoProgress.total_videos) * 100)
                            : 0}%` 
                        }}
                      />
                    </div>
                  </div>

                  {/* Category Progress */}
                  {videoProgress?.category_progress && Object.keys(videoProgress.category_progress).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-white/50 uppercase tracking-wide">Kategoriler</p>
                      {Object.values(videoProgress.category_progress).map((cat, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                          <span className="text-sm text-white/80">{cat.category_title}</span>
                          <span className="text-xs text-purple-400">{cat.watched}/{cat.total}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Milestones */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  Başarılar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {milestones.length > 0 ? milestones.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <span className="text-2xl">{m.icon}</span>
                      <div>
                        <div className="text-white font-medium">{m.title}</div>
                        <div className="text-xs text-green-400">{m.date}</div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-green-400 ml-auto" />
                    </div>
                  )) : (
                    <p className="text-white/50 text-center py-4">Henüz başarı yok</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recordings Tab - Kayitlarim */}
        {activeTab === 'recordings' && (
          <div className="space-y-6">
            {/* Ses Kayitlari */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mic className="w-5 h-5 text-orange-400" />
                  Ses Kayitlarim ({myRecordings?.total || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myRecordings?.recordings?.length > 0 ? (
                  <div className="space-y-3">
                    {myRecordings.recordings.map((rec, idx) => {
                      // Kategori/kaynak belirleme
                      const getSourceInfo = () => {
                        const exerciseId = rec.exercise_id || rec.question_id || "";
                        const category = rec.category || rec.level || "";
                        
                        if (exerciseId.includes("interview") || category === "interview") {
                          return { label: "Mülakat Pratiği", color: "bg-purple-500", icon: "🎙️" };
                        } else if (category === "VOICE_PRACTICE" || exerciseId.includes("voice")) {
                          return { label: "Ses Pratiği", color: "bg-blue-500", icon: "🔊" };
                        } else if (category.match(/^[ABC][0-2]?$/i)) {
                          return { label: `İngilizce - ${category}`, color: "bg-green-500", icon: "🇬🇧" };
                        } else if (exerciseId.includes("cabin") || category.includes("cabin")) {
                          return { label: "Kabin Eğitimi", color: "bg-orange-500", icon: "✈️" };
                        } else if (exerciseId.includes("speaking")) {
                          return { label: "Speaking Testi", color: "bg-pink-500", icon: "💬" };
                        }
                        return { label: category || "Genel", color: "bg-gray-500", icon: "📝" };
                      };
                      const sourceInfo = getSourceInfo();
                      
                      return (
                      <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            {/* Kaynak Etiketi */}
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 ${sourceInfo.color} text-white text-xs rounded-full font-medium`}>
                                {sourceInfo.icon} {sourceInfo.label}
                              </span>
                            </div>
                            <p className="text-white font-medium mb-1">{rec.exercise_title || rec.question_text || 'Kayıt'}</p>
                            <p className="text-white/50 text-xs">{new Date(rec.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                          {(rec.audio_url || rec.audio_data) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-orange-400 hover:text-orange-300"
                              onClick={() => {
                                if (playingAudio === rec.id) {
                                  if (audioRef.current) {
                                    audioRef.current.pause();
                                    audioRef.current = null;
                                  }
                                  setPlayingAudio(null);
                                } else {
                                  if (audioRef.current) {
                                    audioRef.current.pause();
                                  }
                                  const audioSrc = rec.audio_url || rec.audio_data;
                                  audioRef.current = new Audio(audioSrc);
                                  audioRef.current.play().catch(e => console.error('Audio play error:', e));
                                  setPlayingAudio(rec.id);
                                  audioRef.current.onended = () => {
                                    setPlayingAudio(null);
                                    audioRef.current = null;
                                  };
                                }
                              }}
                            >
                              {playingAudio === rec.id ? <Volume2 className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => deleteRecording(rec.id)}
                            data-testid={`delete-recording-${rec.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Mic className="w-12 h-12 mx-auto mb-3 text-white/20" />
                    <p className="text-white/60">Henuz ses kaydiniz yok</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Video Kayitlari */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Video Kayitlarim ({myVideos?.videos?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myVideos?.videos?.length > 0 ? (
                  <div className="space-y-4">
                    {myVideos.videos.map((video, idx) => (
                      <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-start gap-4">
                          {video.video_url && (
                            <div className="w-32 h-24 bg-black rounded-lg overflow-hidden flex-shrink-0">
                              <video 
                                src={video.video_url} 
                                controls 
                                className="w-full h-full object-cover"
                                preload="metadata"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-white font-medium mb-1">{video.question_text || video.question || 'Video Mülakat'}</p>
                            <p className="text-white/50 text-xs mb-2">{new Date(video.created_at).toLocaleDateString('tr-TR')}</p>
                            {video.score && (
                              <div className="flex items-center gap-2">
                                <span className="text-amber-400 font-bold">{video.score}/100</span>
                                <span className="text-white/40 text-xs">Puan</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-white/20" />
                    <p className="text-white/60">Henuz video kaydiniz yok</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Certificates Tab - Belgelerim */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-400" />
                  Belgelerim
                </CardTitle>
                <p className="text-white/60 text-sm">Lutfen asagidaki belgeleri yukleyin</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className={`border rounded-xl p-4 ${myDocuments?.photo ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-orange-500/50 bg-orange-500/10'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${myDocuments?.photo ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
                      {myDocuments?.photo ? <CheckCircle className="w-8 h-8 text-emerald-400" /> : <Camera className="w-8 h-8 text-orange-400" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium mb-1">Vesikalik Fotograf</h4>
                      {myDocuments?.photo ? (<><p className="text-emerald-300 text-sm mb-1">Yuklendi</p><p className="text-white/50 text-xs">{myDocuments.photo.filename}</p></>) : (<p className="text-orange-300 text-sm">Henuz yuklenmedi</p>)}
                    </div>
                    <div>
                      <input type="file" id="photo-upload" accept="image/*" className="hidden" onChange={(e) => handleDocumentUpload(e, 'photo')} disabled={docUploading} />
                      <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10" disabled={docUploading} onClick={() => document.getElementById('photo-upload').click()}>
                        {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        {myDocuments?.photo ? 'Degistir' : 'Yukle'}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className={`border rounded-xl p-4 ${myDocuments?.diploma ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-orange-500/50 bg-orange-500/10'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${myDocuments?.diploma ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
                      {myDocuments?.diploma ? <CheckCircle className="w-8 h-8 text-emerald-400" /> : <GraduationCap className="w-8 h-8 text-orange-400" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium mb-1">Diploma / Ogrenim Belgesi</h4>
                      {myDocuments?.diploma ? (<><p className="text-emerald-300 text-sm mb-1">Yuklendi</p><p className="text-white/50 text-xs">{myDocuments.diploma.filename}</p></>) : (<p className="text-orange-300 text-sm">Henuz yuklenmedi</p>)}
                    </div>
                    <div>
                      <input type="file" id="diploma-upload" accept="image/*,.pdf" className="hidden" onChange={(e) => handleDocumentUpload(e, 'diploma')} disabled={docUploading} />
                      <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10" disabled={docUploading} onClick={() => document.getElementById('diploma-upload').click()}>
                        {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        {myDocuments?.diploma ? 'Degistir' : 'Yukle'}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className={`border rounded-xl p-4 ${myDocuments?.id_card ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-orange-500/50 bg-orange-500/10'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${myDocuments?.id_card ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
                      {myDocuments?.id_card ? <CheckCircle className="w-8 h-8 text-emerald-400" /> : <CreditCard className="w-8 h-8 text-orange-400" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium mb-1">Kimlik Fotokopisi</h4>
                      {myDocuments?.id_card ? (<><p className="text-emerald-300 text-sm mb-1">Yuklendi</p><p className="text-white/50 text-xs">{myDocuments.id_card.filename}</p></>) : (<p className="text-orange-300 text-sm">Henuz yuklenmedi</p>)}
                    </div>
                    <div>
                      <input type="file" id="idcard-upload" accept="image/*,.pdf" className="hidden" onChange={(e) => handleDocumentUpload(e, 'id_card')} disabled={docUploading} />
                      <Button size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10" disabled={docUploading} onClick={() => document.getElementById('idcard-upload').click()}>
                        {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                        {myDocuments?.id_card ? 'Degistir' : 'Yukle'}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-white/70 text-sm">{[myDocuments?.photo, myDocuments?.diploma, myDocuments?.id_card].filter(Boolean).length} / 3 belge yuklendi</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Agreements Tab - Sözleşmeler */}
        {/* Certificates Tab - Sertifikalar */}
        {activeTab === 'certificates' && (
          <div className="space-y-6">
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Belgelerim ({myCertificates?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myCertificates?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myCertificates.map((cert, idx) => (
                      <div key={idx} className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-500/30">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <Award className="w-6 h-6 text-yellow-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-white font-medium mb-1">{cert.program_name || cert.title || 'Sertifika'}</h4>
                            <p className="text-white/60 text-sm mb-2">{cert.total_hours ? cert.total_hours + ' saat' : ''}</p>
                            <p className="text-white/40 text-xs">{cert.issue_date ? new Date(cert.issue_date).toLocaleDateString('tr-TR') : ''}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-yellow-400 hover:text-yellow-300"
                            onClick={() => window.open(cert.download_url || (API + '/certificates/' + cert.id + '/download'), '_blank')}
                          >
                            <Download className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 mx-auto mb-4 text-white/20" />
                    <p className="text-white/60 mb-2">Henuz belgeniz yok</p>
                    <p className="text-white/40 text-sm">Egitimleri tamamladiginizda belgeleriniz burada gorunecek</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Exams Tab */}
        {activeTab === 'exams' && (
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Sınav Sonuçları ({examResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {examResults.length > 0 ? (
                <div className="space-y-3">
                  {examResults.slice(0, 10).map((exam, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                          (exam.score || 0) >= 70 ? 'bg-green-500/20 text-green-400' :
                          (exam.score || 0) >= 50 ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {exam.score || 0}
                        </div>
                        <div>
                          <div className="text-white font-medium">{exam.level_name || exam.level || 'Sınav'}</div>
                          <div className="text-xs text-white/50">{exam.created_at ? new Date(exam.created_at).toLocaleDateString('tr-TR') : '-'}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-white">{exam.correct_answers || 0}/{exam.total_questions || 0}</div>
                        <div className="text-xs text-white/50">Doğru</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Henüz sınav sonucu yok</p>
                  <Button onClick={() => navigate('/english-training')} className="mt-4">Sınava Git</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payments Tab - Ödemelerim */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Ödeme Bilgileri */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-400" />
                  Ödeme Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-white/10 rounded-xl text-center">
                    <p className="text-white/60 text-sm">Toplam Tutar</p>
                    <p className="text-2xl font-bold text-white">{(agreementInfo?.payment_info?.total_fee || 0).toLocaleString('tr-TR')} ₺</p>
                  </div>
                  <div className="p-4 bg-green-500/20 rounded-xl text-center">
                    <p className="text-green-300 text-sm">Ödenen</p>
                    <p className="text-2xl font-bold text-green-400">{(agreementInfo?.payment_info?.total_paid || 0).toLocaleString('tr-TR')} ₺</p>
                  </div>
                  <div className="p-4 bg-orange-500/20 rounded-xl text-center">
                    <p className="text-orange-300 text-sm">Kalan</p>
                    <p className="text-2xl font-bold text-orange-400">{(agreementInfo?.payment_info?.remaining || 0).toLocaleString('tr-TR')} ₺</p>
                  </div>
                </div>

                {/* Ödeme Detayları */}
                {agreementInfo?.payment_info?.payments?.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="text-white/80 font-medium">Ödeme Detayları</h4>
                    {agreementInfo.payment_info.payments.map((payment, idx) => (
                      <div key={idx} className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white font-medium">
                              {payment.payment_type === 'education_fee' ? 'Eğitim Ücreti' :
                               payment.payment_type === 'exam_fee' ? 'Sınav Harcı' :
                               payment.payment_type === 'certificate_fee' ? 'Sertifika Ücreti' : payment.payment_type}
                            </p>
                            <p className="text-white/50 text-sm">{payment.description}</p>
                            {payment.installment_count > 1 && (
                              <p className="text-blue-300 text-xs mt-1">{payment.installment_count} Taksit</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold">{payment.amount?.toLocaleString('tr-TR')} ₺</p>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              payment.status === 'completed' ? 'bg-green-500/30 text-green-300' :
                              payment.status === 'partial' ? 'bg-yellow-500/30 text-yellow-300' :
                              'bg-orange-500/30 text-orange-300'
                            }`}>
                              {payment.status === 'completed' ? 'Ödendi' :
                               payment.status === 'partial' ? 'Kısmi Ödeme' : 'Bekliyor'}
                            </span>
                          </div>
                        </div>
                        {/* Taksitler */}
                        {payment.installments?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                              {payment.installments.map((inst, i) => (
                                <div key={i} className={`p-2 rounded text-center text-xs ${
                                  inst.status === 'paid' ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/60'
                                }`}>
                                  <p className="font-medium">{inst.installment_no}. Taksit</p>
                                  <p>{inst.amount?.toLocaleString('tr-TR')} ₺</p>
                                  {inst.status === 'paid' && <CheckCircle className="w-3 h-3 mx-auto mt-1" />}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/50">Henüz ödeme kaydı bulunmuyor</p>
                  </div>
                )}
                
                {/* Ödeme Yap Butonu */}
                {(agreementInfo?.payment_info?.remaining > 0) && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <Button 
                      onClick={() => setShowPaymentModal(true)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3"
                      data-testid="make-payment-btn"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Ödeme Yap
                    </Button>
                    <p className="text-white/40 text-xs text-center mt-2">
                      Kalan tutar: {(agreementInfo?.payment_info?.remaining || 0).toLocaleString('tr-TR')} ₺
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Kişisel Bilgiler */}
            <Card className="border-0 bg-white/10 backdrop-blur-lg">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-400" />
                  Sözleşmedeki Bilgilerim
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/50 text-sm">Ad Soyad</p>
                      <p className="text-white">{agreementInfo?.user_info?.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-sm">E-posta</p>
                      <p className="text-white">{agreementInfo?.user_info?.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-sm">Telefon</p>
                      <p className="text-white">{agreementInfo?.user_info?.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/50 text-sm">T.C. Kimlik No</p>
                      <p className="text-white">{agreementInfo?.user_info?.tc_no || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-sm">Adres</p>
                      <p className="text-white">{agreementInfo?.user_info?.address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-sm">Şehir</p>
                      <p className="text-white">{agreementInfo?.user_info?.city || '-'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advisor Tab */}
        {activeTab === 'advisor' && (
          <Card className="border-0 bg-white/10 backdrop-blur-lg">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-400" />
                Danışmanım ile İletişim
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Danışman Bilgisi */}
              <div className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-600/20 rounded-xl border border-green-400/30">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-2xl font-bold text-white">
                    D
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Danışman</h3>
                    <p className="text-green-300 text-sm">Eğitim Danışmanınız</p>
                    <p className="text-green-200 text-xs mt-1">Sorularınız için yazabilirsiniz</p>
                  </div>
                </div>
              </div>

              {/* Mesaj Listesi */}
              <div className="space-y-4 max-h-80 overflow-y-auto mb-4 p-4 bg-white/5 rounded-xl">
                {advisorMessages.length > 0 ? (
                  advisorMessages.map((msg, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg max-w-[80%] ${
                        msg.sender === 'student' 
                          ? 'bg-blue-500/30 ml-auto text-right' 
                          : 'bg-green-500/30 mr-auto'
                      }`}
                    >
                      {msg.sender === 'advisor' && msg.advisorName && (
                        <p className="text-green-300 text-xs font-semibold mb-1">
                          {msg.advisorName}
                        </p>
                      )}
                      <p className="text-white text-sm">{msg.message}</p>
                      <p className="text-white/50 text-xs mt-1">
                        {msg.sender === 'student' ? '✓ Gönderildi' : ''} {msg.timestamp}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-white/20 mx-auto mb-2" />
                    <p className="text-white/50">Henüz mesaj yok</p>
                    <p className="text-white/30 text-sm">Danışmanınıza soru sormak için aşağıya yazın</p>
                  </div>
                )}
              </div>

              {/* Mesaj Gönderme */}
              <div className="flex gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Danışmanınıza mesaj yazın..."
                  className="flex-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 resize-none"
                  rows="2"
                />
                <Button 
                  onClick={async () => {
                    if (!newMessage.trim()) return;
                    setSendingMessage(true);
                    try {
                      await axios.post(`${API}/advisor/message`, {
                        message: newMessage
                      }, { withCredentials: true });
                      setAdvisorMessages([...advisorMessages, {
                        sender: 'student',
                        message: newMessage,
                        timestamp: new Date().toLocaleString('tr-TR'),
                        advisorName: null
                      }]);
                      setNewMessage('');
                      toast.success('Mesajınız gönderildi');
                    } catch (err) {
                      toast.error('Mesaj gönderilemedi');
                    }
                    setSendingMessage(false);
                  }}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-green-500 hover:bg-green-600 self-end"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Ödeme Yap Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-400" />
                Ödeme Yap
              </h3>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="text-white/50 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Kalan Tutar Bilgisi */}
              <div className="p-4 bg-gradient-to-r from-green-500/20 to-emerald-600/20 rounded-xl border border-green-400/30">
                <p className="text-green-300 text-sm">Kalan Tutar</p>
                <p className="text-2xl font-bold text-white">
                  {(agreementInfo?.payment_info?.remaining || 0).toLocaleString('tr-TR')} ₺
                </p>
              </div>

              {/* Ödeme Tutarı */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Ödeme Tutarı (₺)</label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Örn: 1000"
                  className="bg-white/10 border-white/20 text-white placeholder-white/50"
                  max={agreementInfo?.payment_info?.remaining || 0}
                  data-testid="payment-amount-input"
                />
              </div>

              {/* Kart Bilgileri */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Kart Üzerindeki İsim</label>
                <Input
                  value={cardHolder}
                  onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                  placeholder="AD SOYAD"
                  className="bg-white/10 border-white/20 text-white placeholder-white/50"
                  data-testid="card-holder-input"
                />
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Kart Numarası</label>
                <Input
                  value={cardNumber}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 16);
                    setCardNumber(val);
                  }}
                  placeholder="1234 5678 9012 3456"
                  className="bg-white/10 border-white/20 text-white placeholder-white/50 font-mono"
                  maxLength={16}
                  data-testid="card-number-input"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Ay</label>
                  <Input
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    placeholder="MM"
                    className="bg-white/10 border-white/20 text-white placeholder-white/50 text-center"
                    maxLength={2}
                    data-testid="exp-month-input"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Yıl</label>
                  <Input
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    placeholder="YY"
                    className="bg-white/10 border-white/20 text-white placeholder-white/50 text-center"
                    maxLength={2}
                    data-testid="exp-year-input"
                  />
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">CVV</label>
                  <Input
                    type="password"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="***"
                    className="bg-white/10 border-white/20 text-white placeholder-white/50 text-center"
                    maxLength={4}
                    data-testid="cvv-input"
                  />
                </div>
              </div>

              {/* Güvenlik Notu */}
              <div className="p-3 bg-white/5 rounded-lg flex items-center gap-2">
                <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-white/50 text-xs">
                  256-bit SSL ile güvenli ödeme. Kart bilgileriniz şifrelenerek iletilir.
                </p>
              </div>

              {/* Ödeme Butonu */}
              <Button
                onClick={async () => {
                  // Validasyon
                  if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
                    toast.error('Geçerli bir tutar girin');
                    return;
                  }
                  if (parseFloat(paymentAmount) > (agreementInfo?.payment_info?.remaining || 0)) {
                    toast.error('Tutar kalan borçtan fazla olamaz');
                    return;
                  }
                  if (!cardHolder.trim()) {
                    toast.error('Kart sahibi adını girin');
                    return;
                  }
                  if (cardNumber.length !== 16) {
                    toast.error('Geçerli bir kart numarası girin');
                    return;
                  }
                  if (expMonth.length !== 2 || parseInt(expMonth) < 1 || parseInt(expMonth) > 12) {
                    toast.error('Geçerli bir ay girin (01-12)');
                    return;
                  }
                  if (expYear.length !== 2) {
                    toast.error('Geçerli bir yıl girin');
                    return;
                  }
                  if (cvv.length < 3) {
                    toast.error('Geçerli bir CVV girin');
                    return;
                  }

                  setProcessingPayment(true);
                  try {
                    const response = await axios.post(`${API}/payments/process`, {
                      amount: parseFloat(paymentAmount),
                      card_holder: cardHolder,
                      card_number: cardNumber,
                      exp_month: expMonth,
                      exp_year: expYear,
                      cvv: cvv
                    }, { withCredentials: true });

                    if (response.data.success) {
                      toast.success('Ödeme başarıyla tamamlandı!');
                      setShowPaymentModal(false);
                      // Reset form
                      setPaymentAmount('');
                      setCardHolder('');
                      setCardNumber('');
                      setExpMonth('');
                      setExpYear('');
                      setCvv('');
                      // Reload data
                      loadAllData();
                    } else {
                      toast.error(response.data.message || 'Ödeme işlemi başarısız');
                    }
                  } catch (err) {
                    console.error('Payment error:', err);
                    toast.error(err.response?.data?.detail || 'Ödeme işlemi sırasında bir hata oluştu');
                  }
                  setProcessingPayment(false);
                }}
                disabled={processingPayment}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3"
                data-testid="submit-payment-btn"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {paymentAmount ? `${parseFloat(paymentAmount).toLocaleString('tr-TR')} ₺ Öde` : 'Ödeme Yap'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sorun Bildir Butonu - Sabit */}
      <button
        onClick={() => setShowReportModal(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-all hover:scale-105"
      >
        <AlertCircle className="w-5 h-5" />
        <span className="hidden sm:inline">Sorun Bildir</span>
      </button>

      {/* Sorun Bildir Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Sorun Bildir
              </h3>
              <button 
                onClick={() => setShowReportModal(false)}
                className="text-white/50 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Sorun Türü */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Sorun Türü</label>
                <div className="flex gap-2">
                  {[
                    { id: 'bug', label: '🐛 Hata' },
                    { id: 'suggestion', label: '💡 Öneri' },
                    { id: 'question', label: '❓ Soru' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setReportType(type.id)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        reportType === type.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <label className="text-white/70 text-sm mb-2 block">Açıklama</label>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Sorunu veya önerinizi detaylı açıklayın..."
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 resize-none"
                  rows="4"
                />
              </div>

              {/* Sayfa Bilgisi */}
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-white/50 text-xs">📍 Bulunduğunuz sayfa: /profile</p>
              </div>

              {/* Gönder Butonu */}
              <Button
                onClick={async () => {
                  if (!reportText.trim()) {
                    toast.error('Lütfen açıklama yazın');
                    return;
                  }
                  setSendingReport(true);
                  try {
                    await axios.post(`${API}/feedback/report`, {
                      type: reportType,
                      message: reportText,
                      page: '/profile',
                      user_agent: navigator.userAgent
                    }, { withCredentials: true });
                    toast.success('Bildiriminiz alındı, teşekkürler!');
                    setShowReportModal(false);
                    setReportText('');
                  } catch (err) {
                    toast.error('Bildirim gönderilemedi');
                  }
                  setSendingReport(false);
                }}
                disabled={sendingReport || !reportText.trim()}
                className="w-full bg-red-500 hover:bg-red-600"
              >
                {sendingReport ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sertifika Detay Modal Bileşeni
function CertificateDetailModal({ certificate, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(
    `${window.location.origin}/verify/${certificate.code}`
  )}`;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/certificates/${certificate.id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AirAcademia_Certificate_${certificate.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF indirildi!');
    } catch (error) {
      toast.error('PDF indirilemedi');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-400" />
            Sertifika Detayı
          </h2>
          <div className="flex gap-2">
            <Button 
              onClick={handleDownload} 
              disabled={downloading}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  İndiriliyor...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  PDF İndir
                </span>
              )}
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-slate-700">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Certificate Preview */}
        <div className="p-6">
          <div className="border-4 border-double border-amber-500 rounded-xl p-6 bg-gradient-to-br from-amber-50 via-white to-blue-50 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full" style={{
                backgroundImage: `radial-gradient(circle at 20px 20px, #f59e0b 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }} />
            </div>
            
            {/* Header */}
            <div className="text-center relative z-10 mb-6">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
                    <path d="M8 24L42 8L32 42L24 28L8 24Z" fill="white" fillOpacity="0.9" />
                    <path d="M8 24L42 8L24 28L8 24Z" fill="white" />
                  </svg>
                </div>
              </div>
              <h1 className="text-xl font-bold">
                <span className="text-blue-600">air</span>
                <span className="text-blue-800">academia</span>
              </h1>
              <p className="text-gray-500 text-xs mt-1">Profesyonel Havacılık Eğitim Akademisi</p>
            </div>

            {/* Certificate Title */}
            <div className="text-center mb-4">
              <h2 className="text-2xl font-serif text-amber-600 mb-1">BAŞARI SERTİFİKASI</h2>
              <p className="text-blue-800 text-sm font-medium">CERTIFICATE OF ACHIEVEMENT</p>
              <div className="h-1 w-20 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto rounded-full mt-2" />
            </div>

            {/* Certification Text */}
            <div className="text-center mb-3">
              <p className="text-gray-600 text-sm">Bu belge, aşağıda ismi yazılı olan kişinin belirtilen eğitimi başarı ile tamamladığını tasdik eder.</p>
            </div>
            <div className="text-center mb-4">
              <p className="text-gray-400 text-xs italic">This certificate confirms that the individual named below has successfully completed the specified training.</p>
            </div>

            {/* Recipient */}
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-amber-400 pb-1 inline-block px-6">
                {certificate.user_name?.toUpperCase()}
              </h3>
            </div>

            {/* Certificate Details */}
            <div className="text-center mb-3">
              <h4 className="text-lg font-bold text-blue-800">{certificate.title}</h4>
              {certificate.title_en && (
                <p className="text-gray-500 text-sm italic">{certificate.title_en}</p>
              )}
              <div className="inline-block mt-2 px-3 py-1 bg-amber-100 rounded-full">
                <span className="text-amber-800 font-medium text-sm">{certificate.category}</span>
              </div>
            </div>

            {/* Description */}
            {certificate.description && (
              <div className="text-center mb-2 px-4">
                <p className="text-gray-600 text-xs leading-relaxed">{certificate.description}</p>
              </div>
            )}
            {certificate.description_en && (
              <div className="text-center mb-4 px-4">
                <p className="text-gray-400 text-xs italic leading-relaxed">{certificate.description_en}</p>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-end justify-between mt-6 pt-4 border-t border-amber-200">
              {/* Date */}
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Veriliş Tarihi</p>
                <p className="font-bold text-gray-800 text-sm">{certificate.issue_date}</p>
                <div className="mt-2 pt-2 border-t border-gray-300 w-28">
                  <p className="text-xs text-gray-500">{certificate.issued_by_name || 'Yetkili İmza'}</p>
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code" 
                  className="w-16 h-16 mx-auto border-2 border-white shadow rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Doğrulama</p>
                <p className="text-xs font-mono font-bold text-amber-600">{certificate.code}</p>
              </div>

              {/* Seal */}
              <div className="text-center">
                <div className="w-14 h-14 rounded-full border-2 border-amber-500 flex items-center justify-center bg-amber-50">
                  <div className="text-center">
                    <Award className="w-5 h-5 text-amber-500 mx-auto" />
                    <p className="text-[5px] text-amber-600 font-bold">CERTIFIED</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Note */}
            <div className="text-center mt-3 pt-2 border-t border-dashed border-gray-300">
              <p className="text-[9px] text-gray-400">
                Bu sertifika {window.location.origin}/verify/{certificate.code} adresinden doğrulanabilir.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Sözleşme Modal */}
      <AgreementModal 
        showModal={showAgreementModal}
        onClose={() => setShowAgreementModal(null)}
        agreementType={showAgreementModal}
        agreementContent={showAgreementModal === "sales" ? myAgreements?.sales_agreement : myAgreements?.distance_agreement}
      />
    </div>
  );
}

export default Profile;
